import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '../api/client';
import type { LetterListResponse, LetterResponse } from '../types/api';
import { LetterState, VaultRole } from '../types/api';
import { parseServerDate } from '../utils/dateUtils';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import SortableList from './SortableList';
import './LettersList.css';
import { fireConfetti } from '../utils/confetti';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

// ── State icons ───────────────────────────────────────────────────────────────

function DraftIcon() {
  return (
    <svg className="letter-lock-icon letter-lock-icon--draft" width="18" height="18"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function SealedIcon() {
  return (
    <svg className="letter-lock-icon letter-lock-icon--sealed" width="18" height="18"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

function UnsealedIcon() {
  return (
    <svg className="letter-lock-icon letter-lock-icon--unsealed" width="18" height="18"
         viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
  );
}

// ── Vote progress bar ─────────────────────────────────────────────────────────

function VoteProgressBar({ yesVotes, totalMembers }: { yesVotes: number; totalMembers: number }) {
  if (totalMembers === 0) {
    return (
      <p className="letter-vote-bar-label letter-vote-bar-label--nudge">
        Invite family members to enable voting
      </p>
    );
  }

  const needed = Math.ceil(totalMembers / 2);
  const thresholdPct = (needed / totalMembers) * 100;
  const fillPct = Math.min((yesVotes / totalMembers) * 100, 100);
  const remaining = Math.max(needed - yesVotes, 0);

  return (
    <div className="letter-vote-bar-wrap">
      <div className="letter-vote-bar-track" role="progressbar"
           aria-valuenow={yesVotes} aria-valuemin={0} aria-valuemax={totalMembers}>
        <div className="letter-vote-bar-fill" style={{ width: `${fillPct}%` }} />
        <div className="letter-vote-bar-threshold" style={{ left: `${thresholdPct}%` }}
             title={`${needed} votes needed`} />
      </div>
      <p className="letter-vote-bar-label">
        {yesVotes} of {totalMembers} {yesVotes === 1 ? 'member has' : 'members have'} voted to unseal
      </p>
      <p className="letter-vote-bar-needed">
        {remaining > 0
          ? `${remaining} more ${remaining === 1 ? 'vote' : 'votes'} needed to unseal`
          : '✓ Threshold reached'}
      </p>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  return parseServerDate(iso)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) ?? '';
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LettersList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [letters, setLetters] = useState<LetterResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const canWrite = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  // Compose / edit form
  const [formMode, setFormMode] = useState<'hidden' | 'create' | 'edit'>('hidden');
  const [editingLetter, setEditingLetter] = useState<LetterResponse | null>(null);
  const [addressedTo, setAddressedTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState<LetterResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Action in-progress tracking
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const loadLetters = useCallback(async () => {
    try {
      const data = await apiGet<LetterListResponse>(`/api/vault/${vaultId}/letters`);
      setLetters(data.letters);
      // Mirror server-side Witness behaviour: Witnesses/Advisors never see Draft letters.
      // In preview mode canWrite is false, so drafts are excluded from the count too.
      const displayed = canWrite
        ? data.letters
        : data.letters.filter(l => l.state !== LetterState.Draft);
      onCountChangeRef.current?.(displayed.length);
      // Auto-expand unsealed letters; auto-expand sealed letters for Witnesses/Spouse so vote buttons are visible
      setExpandedIds(new Set(displayed
        .filter(l => l.state === LetterState.Unsealed ||
                     ((userRole === VaultRole.Witness || userRole === VaultRole.Spouse) &&
                      l.state === LetterState.Sealed))
        .map(l => l.id)));
    } catch (err: any) {
      setError(err.message || 'Failed to load letters.');
    } finally {
      setLoading(false);
    }
  }, [vaultId, userRole, canWrite]);

  useEffect(() => { loadLetters(); }, [loadLetters]);

  async function handleLetterReorder(reordered: LetterResponse[]) {
    setLetters(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/letters/reorder`, {
        orderedIds: reordered.map(l => l.id),
      });
    } catch {
      await loadLetters();
    }
  }

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function openCreate() {
    setEditingLetter(null);
    setAddressedTo('');
    setSubject('');
    setBody('');
    setFormMode('create');
  }

  function openEdit(letter: LetterResponse) {
    setEditingLetter(letter);
    setAddressedTo(letter.addressedTo);
    setSubject(letter.subject);
    setBody(letter.body ?? '');
    setFormMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function closeForm() {
    setFormMode('hidden');
    setEditingLetter(null);
    setAddressedTo('');
    setSubject('');
    setBody('');
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (formMode === 'create') {
        await apiPost(`/api/vault/${vaultId}/letters`, { addressedTo, subject, body });
      } else if (formMode === 'edit' && editingLetter) {
        await apiPut(`/api/vault/${vaultId}/letters/${editingLetter.id}`, { addressedTo, subject, body });
      }
      closeForm();
      await loadLetters();
    } catch (err: any) {
      setError(err.message || 'Failed to save letter.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSeal(letterId: string) {
    setActionInProgress(letterId);
    setError('');
    try {
      await apiPost(`/api/vault/${vaultId}/letters/${letterId}/seal`, {});
      fireConfetti('light');
      await loadLetters();
    } catch (err: any) {
      setError(err.message || 'Failed to seal letter.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleUnseal(letterId: string) {
    setActionInProgress(letterId);
    setError('');
    try {
      await apiPost(`/api/vault/${vaultId}/letters/${letterId}/unseal`, {});
      await loadLetters();
    } catch (err: any) {
      setError(err.message || 'Failed to unseal letter.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleDelete(letter: LetterResponse) {
    setDeleting(true);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/letters/${letter.id}`);
      setConfirmDelete(null);
      await loadLetters();
    } catch (err: any) {
      setError(err.message || 'Failed to delete letter.');
    } finally {
      setDeleting(false);
    }
  }

  async function handleVote(letterId: string, vote: boolean) {
    setActionInProgress(letterId);
    setError('');
    try {
      await apiPost(`/api/vault/${vaultId}/letters/${letterId}/vote`, { vote });
      await loadLetters();
    } catch (err: any) {
      setError(err.message || 'Failed to cast vote.');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleRetractVote(letterId: string) {
    setActionInProgress(letterId);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/letters/${letterId}/vote`);
      await loadLetters();
    } catch (err: any) {
      setError(err.message || 'Failed to retract vote.');
    } finally {
      setActionInProgress(null);
    }
  }

  // Mirror server-side Witness behaviour: filter Draft letters from the displayed list
  // when the viewer doesn't have write access (Witness, Advisor, or preview mode).
  const displayedLetters = canWrite ? letters : letters.filter(l => l.state !== LetterState.Draft);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="letters-list">
      <AlertMessage type="error" message={error} />

      {/* Section header */}
      <div className="letters-header">
        <p className="letters-intro">
          Write a letter privately, then seal it when you're ready. Sealed letters wait for the
          family's vote — or you can unseal them yourself anytime.
        </p>
        {canWrite && formMode === 'hidden' && (
          <button className="btn-text letters-write-btn" onClick={openCreate}>
            + Write a Letter
          </button>
        )}
      </div>

      {/* Compose / edit form */}
      {formMode !== 'hidden' && (
        <form className="letter-form" onSubmit={handleSave}>
          <h3 className="letter-form-title">
            {formMode === 'create' ? 'Write a Letter' : 'Edit Letter'}
          </h3>
          <div className="form-group">
            <label className="form-label" htmlFor="lAddressedTo">Addressed to</label>
            <input
              id="lAddressedTo"
              className="form-input"
              type="text"
              placeholder="e.g., Tami, All of my children, My grandchildren"
              value={addressedTo}
              onChange={e => setAddressedTo(e.target.value)}
              maxLength={200}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="lSubject">Subject</label>
            <input
              id="lSubject"
              className="form-input"
              type="text"
              placeholder="e.g., Things I want you to know"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={500}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="lBody">Your letter</label>
            <textarea
              id="lBody"
              className="form-input letter-body-input"
              placeholder="Write from the heart. Take your time."
              value={body}
              onChange={e => setBody(e.target.value)}
              required
              rows={10}
            />
          </div>
          <div className="letter-form-actions">
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? <LoadingSpinner small /> : 'Save Draft'}
            </button>
            <button className="btn-text" type="button" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      )}

      {/* Empty state */}
      {displayedLetters.length === 0 && formMode === 'hidden' && (
        <EmptyState
          icon="letters"
          title="No letters yet"
          subtitle={canWrite
            ? 'Write a personal letter to someone you love — sealed here for the right moment.'
            : 'No letters have been shared yet.'}
        />
      )}

      {/* Letter cards */}
      <div className="letters-cards">
        <SortableList
          items={displayedLetters}
          canReorder={canWrite}
          onReorder={handleLetterReorder}
          renderItem={(letter) => {
          const isDraft    = letter.state === LetterState.Draft;
          const isSealed   = letter.state === LetterState.Sealed;
          const isUnsealed = letter.state === LetterState.Unsealed;
          const isExpanded = expandedIds.has(letter.id);
          const isMine     = letter.isOwnedByCurrentUser;
          const busy       = actionInProgress === letter.id;

          const cardClass = isDraft    ? 'letter-card--draft'
                          : isUnsealed ? 'letter-card--unsealed'
                          :              'letter-card--sealed';

          const visibilityText = isDraft
            ? 'Private — not yet shared with your vault'
            : isUnsealed && letter.viewerNames.length > 0
              ? `You, ${letter.viewerNames.join(', ')}`
              : isSealed
                ? 'Sealed — visible to all vault members'
                : 'Only you';

          return (
            <div className={`letter-card ${cardClass}`}>

              {/* Card header — always visible */}
              <div className="letter-card-header" onClick={() => toggleExpand(letter.id)}>
                <div className="letter-card-top">
                  <div className="letter-card-lock">
                    {isDraft    && <DraftIcon />}
                    {isSealed   && <SealedIcon />}
                    {isUnsealed && <UnsealedIcon />}
                    <span className={`letter-state-badge letter-state--${isDraft ? 'draft' : isUnsealed ? 'unsealed' : 'sealed'}`}>
                      {isDraft ? 'Draft' : isUnsealed ? 'Unsealed' : 'Sealed'}
                    </span>
                  </div>
                  <span className="letter-card-author">
                    {letter.createdByName ?? 'Unknown'}
                  </span>
                </div>

                <div className="letter-visibility">
                  <span className="letter-visibility-label">Visible to: </span>
                  {visibilityText}
                </div>

                <div className="letter-subject">{letter.subject}</div>

                <div className="letter-card-meta">
                  <span className="letter-addressed-to">To: {letter.addressedTo}</span>
                  <span className="letter-card-date">
                    {isUnsealed && letter.unsealedAt
                      ? `Unsealed ${formatDate(letter.unsealedAt)}`
                      : isSealed && letter.sealedAt
                        ? `Sealed ${formatDate(letter.sealedAt)}`
                        : `Created ${formatDate(letter.createdAt)}`}
                  </span>
                </div>

                {/* Vote bar — sealed letters only */}
                {isSealed && (
                  <VoteProgressBar yesVotes={letter.yesVotes} totalMembers={letter.totalMembers} />
                )}

                <span className="letter-chevron" aria-hidden="true">
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>

              {/* Expanded section */}
              {isExpanded && (
                <div className="letter-card-body">

                  {/* Draft or Unsealed: show letter body */}
                  {(isDraft || isUnsealed) && letter.body && (
                    <div className="letter-body-text">{letter.body}</div>
                  )}

                  {/* Sealed: show vote detail + vote actions */}
                  {isSealed && (
                    <div className="letter-vote-section">
                      {letter.votes.length > 0 && (
                        <div className="letter-vote-list">
                          {letter.votes.map(v => (
                            <span key={v.id}
                                  className={`letter-vote-chip ${v.vote ? 'letter-vote-chip--yes' : 'letter-vote-chip--no'}`}>
                              {v.memberName ?? 'Member'}: {v.vote ? 'Unseal' : 'Keep sealed'}
                            </span>
                          ))}
                        </div>
                      )}

                      {isMine && letter.votes.length === 0 && (
                        <p className="letter-vote-waiting">
                          Waiting for your vault members to vote. You can unseal this letter yourself at any time.
                        </p>
                      )}

                      {/* Vote actions — Owner/Spouse/Witness can vote; never shown on your own letter
                          (authors use the Unseal button in the action bar instead) */}
                      {userRole !== VaultRole.Advisor && !isMine && (
                        <div className="letter-vote-actions">
                          {letter.currentUserVote === null ? (
                            <>
                              <button className="letter-vote-btn letter-vote-btn--yes"
                                      onClick={() => handleVote(letter.id, true)}
                                      disabled={busy}>
                                {busy ? <LoadingSpinner small /> : 'Vote to Unseal'}
                              </button>
                              <button className="letter-vote-btn letter-vote-btn--no"
                                      onClick={() => handleVote(letter.id, false)}
                                      disabled={busy}>
                                Keep Sealed
                              </button>
                            </>
                          ) : (
                            <>
                              <span className="letter-my-vote">
                                Your vote: <strong>{letter.currentUserVote ? 'Unseal' : 'Keep sealed'}</strong>
                              </span>
                              <button className="btn-text"
                                      onClick={() => handleRetractVote(letter.id)}
                                      disabled={busy}>
                                Change vote
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action bar — letter author only */}
              {isMine && (
                <div className="letter-card-actions">

                  {/* Draft: write privately → seal when ready → delete if abandoned */}
                  {isDraft && (
                    <>
                      <button className="vh-action-link" onClick={() => openEdit(letter)}>Edit</button>
                      <button className="vh-action-link letter-seal-btn"
                              onClick={() => handleSeal(letter.id)} disabled={busy}>
                        {busy ? 'Sealing…' : '🔒 Seal this Letter'}
                      </button>
                      <button className="vh-action-link letter-delete-btn"
                              onClick={() => setConfirmDelete(letter)}>
                        Delete
                      </button>
                    </>
                  )}

                  {/* Sealed: author can unseal directly or edit without revealing body */}
                  {isSealed && (
                    <>
                      <button className="vh-action-link" onClick={() => openEdit(letter)}>Edit</button>
                      <button className="vh-action-link" onClick={() => handleUnseal(letter.id)} disabled={busy}>
                        {busy ? 'Unsealing…' : 'Unseal'}
                      </button>
                    </>
                  )}

                  {/* Unsealed: author can edit or re-seal */}
                  {isUnsealed && (
                    <>
                      <button className="vh-action-link" onClick={() => openEdit(letter)}>Edit</button>
                      <button className="vh-action-link letter-seal-btn"
                              onClick={() => handleSeal(letter.id)} disabled={busy}>
                        {busy ? 'Resealing…' : '🔒 Re-Seal Letter'}
                      </button>
                    </>
                  )}

                </div>
              )}
            </div>
          );
        }}
        />
      </div>

      {/* Delete confirm — only reachable for Draft letters */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Delete this draft?</h3>
            <p>"{confirmDelete.subject}" to {confirmDelete.addressedTo} will be permanently deleted. This cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete)} disabled={deleting}>
                {deleting ? <LoadingSpinner small /> : 'Delete Draft'}
              </button>
              <button className="btn-text" onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
